<template>
  <div
    class="textPrimary flex flex-column align-items-center justify-content-center md:w-auto"
    style="width: 40rem !important"
  >
    <h2 class="text-center">Modifique los datos del Modelo</h2>
    <div class="w-6 mb-3 blue-line"></div>

    <div v-if="loading" class="spinner-wrapper">
      <ProgressSpinner style="width: 50px; height: 50px" />
    </div>

    <div v-else class="w-full">
      <div class="formgrid grid">
        <div class="col-12 md:col-6 flex flex-column gap-4">
          <div class="flex flex-column gap-2">
            <label for="categoria">Seleccione una categoría</label>
            <Dropdown
              id="categoria"
              v-model="modelo.id_categoria"
              :options="categorias"
              optionLabel="Nombre"
              optionValue="CategoriaID"
              :filter="true"
              class="w-full input-border-blue"
            />
          </div>

          <div class="flex flex-column gap-2">
            <label for="subcategoria">Seleccione una sub-categoría</label>
            <Dropdown
              id="subcategoria"
              v-model="modelo.id_subcategoria"
              :options="subcategorias"
              optionLabel="Nombre"
              optionValue="SubcategoriaID"
              :filter="true"
              class="w-full input-border-blue"
            />
          </div>

          <div class="flex flex-column gap-2">
            <label for="nombre">Ingrese el nombre</label>
            <InputText
              v-no-emoji
              id="nombre"
              v-model="modelo.nombre"
              class="w-full input-border-blue"
            />
          </div>
        </div>

        <div class="col-12 md:col-6 flex flex-column gap-4">
          <div class="flex flex-column gap-2">
            <label for="marca">Seleccione una marca</label>
            <Dropdown
              id="marca"
              v-model="modelo.id_marca"
              :options="marcas"
              optionLabel="nombre"
              optionValue="MarcaID"
              :filter="true"
              class="w-full input-border-blue"
            />
          </div>

          <div class="flex flex-column gap-2">
            <label for="tipo">Seleccione un tipo</label>
            <Dropdown
              id="tipo"
              v-model="modelo.id_tipo"
              :options="tipos"
              optionLabel="nombre"
              optionValue="tipoID"
              :filter="true"
              class="w-full input-border-blue"
            />
          </div>
        </div>
      </div>

      <div class="mt-4 flex justify-content-end gap-2">
        <Button label="Cancelar" @click="handleCancel" class="cancelar-button" />
        <Button
          label="Confirmar"
          :icon="isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
          @click="handleSave"
          :disabled="isSaving"
          class="custom-action-button"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onBeforeMount, defineComponent } from "vue";
import { useStoreCompat } from "@/composables/useStoreCompat.js";
import { useRest } from "@/composables/useRest";

export default defineComponent({
  name: "ModeloForm",
  props: {
    modeloData: Object,
  },
  emits: ["close", "openModalsuccess"],
  setup(props, { emit }) {
    const store = useStoreCompat();
    const rest = useRest();

    const modalSuccess = ref(null);
    const modelo = ref({
      nombre: props.modeloData?.Nombre || "",
      id_marca: props.modeloData.id_marca || null,
      categoria: props.modeloData?.categorias || "",
      id_categoria: props.modeloData?.id_categoria || null,
      id_subcategoria: props.modeloData.id_subcategoria || null,
      subcategoria: props.modeloData.subcategorias || null,
      tipo: props.modeloData.tipo || null,
      id_tipo: props.modeloData.id_tipo || null,
    });
    const modeloID = ref(props.modeloData.ModeloID);

    const loading = ref(true);
    const isSaving = ref(false);

    const categorias = computed(() => store.state.siis.categoriasDeposito);
    const subcategorias = computed(() => store.state.siis.subcategoriasDeposito);
    const marcas = computed(() => store.state.siis.marca);
    const tipos = computed(() => store.state.siis.tipoDeposito);

    const changeDropdown = (event) => {
      const selected = event.value;
      const found = categorias.value.find((sub) => sub.CategoriaID === selected);
      modelo.value.categoria = found?.Nombre || "";
      modelo.value.id_categoria = found?.CategoriaID || null;
    };

    const handleSave = async () => {
      isSaving.value = true;
      try {
        await rest.put({
          url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/parametros/modelos/${modeloID.value}`,
          formData: {
            Nombre: modelo.value.nombre,
            id_categoria: modelo.value.id_categoria,
            id_subcategoria: modelo.value.id_subcategoria,
            id_marca: modelo.value.id_marca,
            id_tipo: modelo.value.id_tipo,
          },
        });
        emit("openModalsuccess", {
          title: "Los datos del modelo fueron editados",
          message: "Ahora se veran reflejados tus nuevos datos en la tabla",
        });
        emit("close");
      } catch (err) {
        console.error("Error al guardar:", err);
      } finally {
        isSaving.value = false;
      }
    };

    const handleCancel = () => {
      emit("close");
    };

    onBeforeMount(async () => {
      await store.dispatch("siis/setSubCategoriasDeposito");
      await store.dispatch("siis/setCategoriasDeposito");
      await store.dispatch("siis/setTipoDeposito");
      await store.dispatch("siis/setMarca");
      loading.value = false;
    });

    return {
      //refs
      modalSuccess,
      modelo,
      loading,
      isSaving,
      //computetd
      categorias,
      subcategorias,
      marcas,
      tipos,
      //methods
      handleSave,
      changeDropdown,
      handleCancel,
    };
  },
});
</script>

<style scoped>
.textPrimary {
  color: #111928;
}

.blue-line {
  height: 4px;
  background-color: #0b5195;
  border-radius: 9999px;
}

.custom-action-button {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background-color: var(--Primary, #0b5195);
  color: white;
  border: none;
}

.custom-action-button:hover {
  opacity: 0.9;
  cursor: pointer;
}

.cancelar-button {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background: var(--White-White, #fff);
  color: #0b5195;
  border: none;
  box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4);
}

.cancelar-button:hover {
  background: #f4f4f4 !important;
  color: #0b5195 !important;
  opacity: 0.9;
  cursor: pointer;
}

.input-border-blue {
  border: 2px solid #0b5195;
  border-radius: 6px;
  width: 100%;
  transition: border 0.2s ease;
}
</style>
